import { useEffect, useState, useCallback } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Switch } from '@/components/ui/switch';
import {
  Users, Plus, Pencil, Trash2, Clock, DollarSign, Calculator, FileText,
  Phone, Mail, Calendar, ChevronRight, AlertTriangle, CheckCircle
} from 'lucide-react';
import { toast } from 'sonner';

const PAY_TYPES = { hourly: 'Hourly', salary: 'Salary', piece_rate: 'Piece Rate', commission: 'Commission' };
const PAY_COLORS = { hourly: 'bg-blue-50 text-blue-700', salary: 'bg-violet-50 text-violet-700', piece_rate: 'bg-amber-50 text-amber-700', commission: 'bg-emerald-50 text-emerald-700' };
const DEPTS = ['field', 'office', 'sales', 'management', 'subcontractor'];
const EMPTY_EMP = { name: '', email: '', phone: '', role_title: '', department: 'field', pay_type: 'hourly', pay_rate: 0, salary_annual: 0, piece_rate_per_unit: 0, commission_pct: 0, hire_date: '', address: '', emergency_contact: '', emergency_phone: '', filing_status: 'single', allowances: 1, notes: '' };
const EMPTY_TIME = { employee_id: '', employee_name: '', project_id: '', project_name: '', date: '', hours: 8, overtime_hours: 0, pieces: 0, description: '' };

export default function Payroll() {
  const [tab, setTab] = useState('employees');
  const [employees, setEmployees] = useState([]);
  const [projects, setProjects] = useState([]);
  const [timeEntries, setTimeEntries] = useState([]);
  const [payrollResult, setPayrollResult] = useState(null);
  const [payrollHistory, setPayrollHistory] = useState([]);
  // Dialogs
  const [empDialog, setEmpDialog] = useState(false);
  const [editEmp, setEditEmp] = useState(null);
  const [empForm, setEmpForm] = useState(EMPTY_EMP);
  const [timeDialog, setTimeDialog] = useState(false);
  const [timeForm, setTimeForm] = useState(EMPTY_TIME);
  const [periodStart, setPeriodStart] = useState('');
  const [periodEnd, setPeriodEnd] = useState('');
  const [historyDetail, setHistoryDetail] = useState(null);
  const [ptoRequests, setPtoRequests] = useState([]);
  const [ptoDialog, setPtoDialog] = useState(false);
  const [ptoForm, setPtoForm] = useState({ employee_id: '', employee_name: '', start_date: '', end_date: '', hours: 8, type: 'vacation', reason: '' });

  const load = useCallback(() => {
    api.get('/payroll/employees').then(r => setEmployees(r.data)).catch(() => {});
    api.get('/projects').then(r => { const d = Array.isArray(r.data) ? r.data : r.data.data || []; setProjects(d); }).catch(() => {});
    api.get('/payroll/history').then(r => setPayrollHistory(r.data)).catch(() => {});
    api.get('/company/pto-requests?status=all').then(r => setPtoRequests(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const loadTime = useCallback(() => {
    const params = {};
    if (periodStart && periodEnd) { params.period_start = periodStart; params.period_end = periodEnd; }
    api.get('/payroll/time-entries', { params }).then(r => setTimeEntries(r.data)).catch(() => {});
  }, [periodStart, periodEnd]);
  useEffect(() => { if (periodStart && periodEnd) loadTime(); }, [loadTime, periodStart, periodEnd]);

  // Set default period to current biweekly
  useEffect(() => {
    const now = new Date();
    const dayOfMonth = now.getDate();
    const start = dayOfMonth <= 15 ? 1 : 16;
    const endDay = dayOfMonth <= 15 ? 15 : new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
    const s = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(start).padStart(2, '0')}`;
    const e = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(endDay).padStart(2, '0')}`;
    setPeriodStart(s); setPeriodEnd(e);
  }, []);

  const saveEmp = async () => {
    try {
      const payload = { ...empForm, pay_rate: parseFloat(empForm.pay_rate) || 0, salary_annual: parseFloat(empForm.salary_annual) || 0, piece_rate_per_unit: parseFloat(empForm.piece_rate_per_unit) || 0, commission_pct: parseFloat(empForm.commission_pct) || 0, allowances: parseInt(empForm.allowances) || 1 };
      if (editEmp) { await api.put(`/payroll/employees/${editEmp.id}`, payload); toast.success('Employee updated'); }
      else { await api.post('/payroll/employees', payload); toast.success('Employee added'); }
      setEmpDialog(false); setEditEmp(null); setEmpForm(EMPTY_EMP); load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const saveTime = async () => {
    try {
      await api.post('/payroll/time-entries', timeForm);
      toast.success('Time entry logged');
      setTimeDialog(false); setTimeForm(EMPTY_TIME); loadTime();
    } catch { toast.error('Failed'); }
  };

  const runPayroll = async () => {
    try {
      const calc = await api.post('/payroll/calculate', { period_start: periodStart, period_end: periodEnd });
      setPayrollResult(calc.data);
    } catch { toast.error('Calculation failed'); }
  };

  const finalizePayroll = async () => {
    if (!window.confirm('Finalize this payroll? This will be recorded permanently.')) return;
    try {
      await api.post('/payroll/run', { period_start: periodStart, period_end: periodEnd });
      toast.success('Payroll finalized and recorded');
      setPayrollResult(null); load();
    } catch { toast.error('Failed'); }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-[1400px] mx-auto" data-testid="payroll-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-[#1e3a50] tracking-tight">Payroll & Employees</h1>
          <p className="text-[#6b8a9e] text-sm font-medium mt-0.5">{employees.length} employees | Bi-weekly pay period</p>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-6 bg-white p-1 rounded-lg shadow-sm border border-[#d4e6ef]/50">
          <TabsTrigger value="employees" className="data-[state=active]:bg-[#4a9ac5] data-[state=active]:text-white gap-1.5 font-bold text-xs">
            <Users className="w-3.5 h-3.5" /> Employees
          </TabsTrigger>
          <TabsTrigger value="time" className="data-[state=active]:bg-[#4a9ac5] data-[state=active]:text-white gap-1.5 font-bold text-xs">
            <Clock className="w-3.5 h-3.5" /> Time Tracking
          </TabsTrigger>
          <TabsTrigger value="payroll" className="data-[state=active]:bg-[#4a9ac5] data-[state=active]:text-white gap-1.5 font-bold text-xs">
            <DollarSign className="w-3.5 h-3.5" /> Run Payroll
          </TabsTrigger>
          <TabsTrigger value="history" className="data-[state=active]:bg-[#4a9ac5] data-[state=active]:text-white gap-1.5 font-bold text-xs">
            <FileText className="w-3.5 h-3.5" /> History
          </TabsTrigger>
          <TabsTrigger value="pto" className="data-[state=active]:bg-[#4a9ac5] data-[state=active]:text-white gap-1.5 font-bold text-xs">
            <Calendar className="w-3.5 h-3.5" /> PTO
          </TabsTrigger>
        </TabsList>

        {/* EMPLOYEES TAB */}
        <TabsContent value="employees">
          <div className="flex justify-between mb-4">
            <p className="text-sm font-bold text-[#2a4a60]">{employees.length} team members</p>
            <Button className="bg-[#4a9ac5] hover:bg-[#3d87b3] text-white" onClick={() => { setEditEmp(null); setEmpForm(EMPTY_EMP); setEmpDialog(true); }} data-testid="add-emp-btn">
              <Plus className="w-4 h-4 mr-1" /> Add Employee
            </Button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {employees.map(emp => (
              <Card key={emp.id} className="border-0 shadow-[0_1px_3px_rgba(0,0,0,0.04)] hover:shadow-md transition-shadow" data-testid={`emp-card-${emp.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <p className="text-sm font-bold text-[#1e3a50]">{emp.name}</p>
                      <p className="text-[11px] font-medium text-[#6b8a9e]">{emp.role_title || emp.department}</p>
                    </div>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => { setEditEmp(emp); setEmpForm(emp); setEmpDialog(true); }}><Pencil className="w-3 h-3 text-gray-400" /></Button>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 mb-2">
                    <Badge className={`text-[10px] font-bold ${PAY_COLORS[emp.pay_type] || PAY_COLORS.hourly}`}>{PAY_TYPES[emp.pay_type]}</Badge>
                    <span className="text-xs font-bold font-mono text-[#2a6496]">
                      {emp.pay_type === 'hourly' ? `$${emp.pay_rate}/hr` : emp.pay_type === 'salary' ? `$${(emp.salary_annual || 0).toLocaleString()}/yr` : emp.pay_type === 'piece_rate' ? `$${emp.piece_rate_per_unit}/pc` : `${emp.commission_pct}%`}
                    </span>
                  </div>
                  <div className="space-y-0.5 text-[10px] font-medium text-[#6b8a9e]">
                    {emp.phone && <p className="flex items-center gap-1"><Phone className="w-2.5 h-2.5" />{emp.phone}</p>}
                    {emp.email && <p className="flex items-center gap-1"><Mail className="w-2.5 h-2.5" />{emp.email}</p>}
                    {emp.hire_date && <p className="flex items-center gap-1"><Calendar className="w-2.5 h-2.5" />Hired: {emp.hire_date}</p>}
                  </div>
                </CardContent>
              </Card>
            ))}
            {employees.length === 0 && <p className="col-span-3 text-center py-12 text-sm font-medium text-gray-400">No employees yet</p>}
          </div>
        </TabsContent>

        {/* TIME TRACKING TAB */}
        <TabsContent value="time">
          <div className="flex flex-wrap items-end gap-3 mb-4">
            <div><Label className="text-xs font-bold">Period Start</Label><Input type="date" value={periodStart} onChange={e => setPeriodStart(e.target.value)} className="w-40" /></div>
            <div><Label className="text-xs font-bold">Period End</Label><Input type="date" value={periodEnd} onChange={e => setPeriodEnd(e.target.value)} className="w-40" /></div>
            <Button className="bg-[#4a9ac5] hover:bg-[#3d87b3] text-white" onClick={() => { setTimeForm({ ...EMPTY_TIME, date: new Date().toISOString().split('T')[0] }); setTimeDialog(true); }} data-testid="add-time-btn">
              <Plus className="w-4 h-4 mr-1" /> Log Time
            </Button>
          </div>
          <Card className="border-0 shadow-[0_1px_3px_rgba(0,0,0,0.04)]">
            <Table>
              <TableHeader>
                <TableRow className="bg-[#f5f9fb]">
                  <TableHead className="font-bold text-xs text-[#2a4a60]">Employee</TableHead>
                  <TableHead className="font-bold text-xs text-[#2a4a60]">Date</TableHead>
                  <TableHead className="font-bold text-xs text-[#2a4a60]">Project</TableHead>
                  <TableHead className="font-bold text-xs text-[#2a4a60] text-center">Hours</TableHead>
                  <TableHead className="font-bold text-xs text-[#2a4a60] text-center">OT</TableHead>
                  <TableHead className="font-bold text-xs text-[#2a4a60]">Notes</TableHead>
                  <TableHead className="w-16" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {timeEntries.map(entry => (
                  <TableRow key={entry.id} className="hover:bg-[#f5f9fb]">
                    <TableCell className="text-xs font-bold text-[#1e3a50]">{entry.employee_name}</TableCell>
                    <TableCell className="text-xs font-mono font-medium text-[#6b8a9e]">{entry.date}</TableCell>
                    <TableCell className="text-xs font-medium text-[#4a6a7e]">{entry.project_name || '—'}</TableCell>
                    <TableCell className="text-center text-xs font-bold font-mono">{entry.hours}</TableCell>
                    <TableCell className="text-center text-xs font-mono text-orange-600">{entry.overtime_hours > 0 ? entry.overtime_hours : '—'}</TableCell>
                    <TableCell className="text-[10px] text-gray-400 max-w-[150px] truncate">{entry.description}</TableCell>
                    <TableCell>
                      <Button variant="ghost" size="icon" className="h-6 w-6" onClick={async () => { await api.delete(`/payroll/time-entries/${entry.id}`); loadTime(); toast.success('Deleted'); }}>
                        <Trash2 className="w-3 h-3 text-red-400" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {timeEntries.length === 0 && <p className="text-center py-8 text-sm font-medium text-gray-400">No time entries for this period</p>}
          </Card>
        </TabsContent>

        {/* RUN PAYROLL TAB */}
        <TabsContent value="payroll">
          <div className="flex flex-wrap items-end gap-3 mb-6">
            <div><Label className="text-xs font-bold">Period Start</Label><Input type="date" value={periodStart} onChange={e => setPeriodStart(e.target.value)} className="w-40" /></div>
            <div><Label className="text-xs font-bold">Period End</Label><Input type="date" value={periodEnd} onChange={e => setPeriodEnd(e.target.value)} className="w-40" /></div>
            <Button className="bg-[#4a9ac5] hover:bg-[#3d87b3] text-white" onClick={runPayroll} data-testid="calc-payroll-btn">
              <Calculator className="w-4 h-4 mr-1" /> Calculate Payroll
            </Button>
            {payrollResult && (
              <Button className="bg-emerald-500 hover:bg-emerald-600 text-white" onClick={finalizePayroll} data-testid="finalize-payroll-btn">
                <CheckCircle className="w-4 h-4 mr-1" /> Finalize & Record
              </Button>
            )}
          </div>
          {payrollResult && (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
                <Card className="border-0 shadow-[0_1px_3px_rgba(0,0,0,0.04)]"><CardContent className="p-4 text-center">
                  <p className="text-2xl font-extrabold font-mono text-[#1e3a50]">${payrollResult.summary.total_gross.toLocaleString()}</p>
                  <p className="text-[10px] font-bold text-[#6b8a9e]">TOTAL GROSS</p>
                </CardContent></Card>
                <Card className="border-0 shadow-[0_1px_3px_rgba(0,0,0,0.04)]"><CardContent className="p-4 text-center">
                  <p className="text-2xl font-extrabold font-mono text-red-600">${payrollResult.summary.total_deductions.toLocaleString()}</p>
                  <p className="text-[10px] font-bold text-[#6b8a9e]">DEDUCTIONS</p>
                </CardContent></Card>
                <Card className="border-0 shadow-[0_1px_3px_rgba(0,0,0,0.04)]"><CardContent className="p-4 text-center">
                  <p className="text-2xl font-extrabold font-mono text-emerald-600">${payrollResult.summary.total_net.toLocaleString()}</p>
                  <p className="text-[10px] font-bold text-[#6b8a9e]">NET PAY</p>
                </CardContent></Card>
                <Card className="border-0 shadow-[0_1px_3px_rgba(0,0,0,0.04)]"><CardContent className="p-4 text-center">
                  <p className="text-2xl font-extrabold font-mono text-[#2a6496]">{payrollResult.summary.employee_count}</p>
                  <p className="text-[10px] font-bold text-[#6b8a9e]">EMPLOYEES</p>
                </CardContent></Card>
              </div>
              <Card className="border-0 shadow-[0_1px_3px_rgba(0,0,0,0.04)]">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-[#f5f9fb]">
                      <TableHead className="font-bold text-xs">Employee</TableHead>
                      <TableHead className="font-bold text-xs">Type</TableHead>
                      <TableHead className="font-bold text-xs text-center">Hours</TableHead>
                      <TableHead className="font-bold text-xs text-center">OT</TableHead>
                      <TableHead className="font-bold text-xs text-right">Gross</TableHead>
                      <TableHead className="font-bold text-xs text-right">Deductions</TableHead>
                      <TableHead className="font-bold text-xs text-right">Net Pay</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {payrollResult.employees.map(emp => (
                      <TableRow key={emp.employee_id}>
                        <TableCell><p className="text-xs font-bold text-[#1e3a50]">{emp.employee_name}</p><p className="text-[9px] text-[#6b8a9e]">{emp.role_title}</p></TableCell>
                        <TableCell><Badge className={`text-[9px] font-bold ${PAY_COLORS[emp.pay_type]}`}>{PAY_TYPES[emp.pay_type]}</Badge></TableCell>
                        <TableCell className="text-center text-xs font-mono font-bold">{emp.total_hours || '—'}</TableCell>
                        <TableCell className="text-center text-xs font-mono text-orange-600">{emp.overtime_hours > 0 ? emp.overtime_hours : '—'}</TableCell>
                        <TableCell className="text-right text-xs font-mono font-bold">${emp.gross_pay.toLocaleString()}</TableCell>
                        <TableCell className="text-right text-xs font-mono text-red-500">-${emp.deductions.total_deductions.toLocaleString()}</TableCell>
                        <TableCell className="text-right text-xs font-mono font-bold text-emerald-700">${emp.net_pay.toLocaleString()}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Card>
            </>
          )}
          {!payrollResult && <p className="text-center py-12 text-sm font-medium text-gray-400">Select a pay period and click Calculate Payroll</p>}
        </TabsContent>

        {/* HISTORY TAB */}
        <TabsContent value="history">
          <div className="space-y-3">
            {payrollHistory.map(run => (
              <Card key={run.id} className="border-0 shadow-[0_1px_3px_rgba(0,0,0,0.04)] cursor-pointer hover:shadow-md transition-shadow"
                onClick={async () => { const r = await api.get(`/payroll/history/${run.id}`); setHistoryDetail(r.data); }} data-testid={`payroll-run-${run.id}`}>
                <CardContent className="p-4 flex items-center justify-between">
                  <div>
                    <p className="text-sm font-bold text-[#1e3a50]">{run.period_start} — {run.period_end}</p>
                    <p className="text-[10px] font-medium text-[#6b8a9e]">{run.summary?.employee_count} employees | Run {new Date(run.run_at).toLocaleDateString()}</p>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="text-base font-extrabold font-mono text-emerald-700">${(run.summary?.total_net || 0).toLocaleString()}</p>
                      <p className="text-[9px] font-bold text-[#6b8a9e]">NET PAY</p>
                    </div>
                    <Badge className="bg-emerald-50 text-emerald-700 text-[10px] font-bold">{run.status}</Badge>
                    <ChevronRight className="w-4 h-4 text-gray-300" />
                  </div>
                </CardContent>
              </Card>
            ))}
            {payrollHistory.length === 0 && <p className="text-center py-12 text-sm font-medium text-gray-400">No payroll runs yet</p>}
          </div>
        </TabsContent>

        {/* PTO TAB */}
        <TabsContent value="pto">
          <div className="flex justify-between mb-4">
            <p className="text-sm font-bold text-[#2a4a60]">{ptoRequests.filter(r => r.status === 'pending').length} pending requests</p>
            <Button className="bg-[#4a9ac5] hover:bg-[#3d87b3] text-white" onClick={() => { setPtoForm({ employee_id: '', employee_name: '', start_date: '', end_date: '', hours: 8, type: 'vacation', reason: '' }); setPtoDialog(true); }} data-testid="request-pto-btn">
              <Plus className="w-4 h-4 mr-1" /> Request Time Off
            </Button>
          </div>
          <div className="space-y-2">
            {ptoRequests.map(req => (
              <Card key={req.id} className="border-0 shadow-[0_1px_3px_rgba(0,0,0,0.04)]" data-testid={`pto-${req.id}`}>
                <CardContent className="p-3 flex items-center gap-4">
                  <div className="w-9 h-9 rounded-lg bg-violet-50 flex items-center justify-center flex-shrink-0">
                    <Calendar className="w-4 h-4 text-violet-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-[#1e3a50]">{req.employee_name}</p>
                    <p className="text-[11px] font-medium text-[#6b8a9e]">{req.start_date} — {req.end_date} | {req.hours}hrs | <span className="capitalize">{req.type}</span></p>
                    {req.reason && <p className="text-[10px] text-gray-400">{req.reason}</p>}
                  </div>
                  <Badge className={`text-[10px] font-bold ${req.status === 'approved' ? 'bg-emerald-50 text-emerald-700' : req.status === 'denied' ? 'bg-red-50 text-red-600' : 'bg-amber-50 text-amber-700'}`}>{req.status}</Badge>
                  {req.status === 'pending' && (
                    <div className="flex gap-1">
                      <Button size="sm" className="h-7 text-[10px] bg-emerald-500 hover:bg-emerald-600 text-white" onClick={async () => {
                        await api.put(`/company/pto-requests/${req.id}`, { status: 'approved' }); toast.success('Approved');
                        api.get('/company/pto-requests?status=all').then(r => setPtoRequests(r.data));
                      }}>Approve</Button>
                      <Button size="sm" variant="outline" className="h-7 text-[10px] text-red-500 border-red-200" onClick={async () => {
                        await api.put(`/company/pto-requests/${req.id}`, { status: 'denied' }); toast.success('Denied');
                        api.get('/company/pto-requests?status=all').then(r => setPtoRequests(r.data));
                      }}>Deny</Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
            {ptoRequests.length === 0 && <p className="text-center py-12 text-sm font-medium text-gray-400">No PTO requests</p>}
          </div>
        </TabsContent>
      </Tabs>

      {/* PTO Request Dialog */}
      <Dialog open={ptoDialog} onOpenChange={setPtoDialog}>
        <DialogContent className="max-w-md" data-testid="pto-dialog">
          <DialogHeader><DialogTitle>Request Time Off</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs font-bold">Employee</Label>
              <Select value={ptoForm.employee_id} onValueChange={v => { const emp = employees.find(e => e.id === v); setPtoForm(p => ({...p, employee_id: v, employee_name: emp?.name || ''})); }}>
                <SelectTrigger><SelectValue placeholder="Select employee..." /></SelectTrigger>
                <SelectContent>{employees.map(e => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs font-bold">Start Date</Label><Input type="date" value={ptoForm.start_date} onChange={e => setPtoForm(p => ({...p, start_date: e.target.value}))} /></div>
              <div><Label className="text-xs font-bold">End Date</Label><Input type="date" value={ptoForm.end_date} onChange={e => setPtoForm(p => ({...p, end_date: e.target.value}))} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs font-bold">Hours</Label><Input type="number" value={ptoForm.hours} onChange={e => setPtoForm(p => ({...p, hours: parseFloat(e.target.value) || 0}))} /></div>
              <div><Label className="text-xs font-bold">Type</Label>
                <Select value={ptoForm.type} onValueChange={v => setPtoForm(p => ({...p, type: v}))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="vacation">Vacation</SelectItem>
                    <SelectItem value="sick">Sick</SelectItem>
                    <SelectItem value="personal">Personal</SelectItem>
                    <SelectItem value="bereavement">Bereavement</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div><Label className="text-xs font-bold">Reason (optional)</Label><Input value={ptoForm.reason} onChange={e => setPtoForm(p => ({...p, reason: e.target.value}))} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPtoDialog(false)}>Cancel</Button>
            <Button className="bg-[#4a9ac5] hover:bg-[#3d87b3] text-white" onClick={async () => {
              await api.post('/company/pto-request', ptoForm); toast.success('Request submitted');
              setPtoDialog(false); api.get('/company/pto-requests?status=all').then(r => setPtoRequests(r.data));
            }} disabled={!ptoForm.employee_id || !ptoForm.start_date}>Submit Request</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Employee Dialog */}
      <Dialog open={empDialog} onOpenChange={setEmpDialog}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="emp-dialog">
          <DialogHeader><DialogTitle>{editEmp ? 'Edit Employee' : 'Add Employee'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs font-bold">Full Name *</Label><Input value={empForm.name} onChange={e => setEmpForm(p => ({...p, name: e.target.value}))} data-testid="emp-name" /></div>
              <div><Label className="text-xs font-bold">Job Title</Label><Input value={empForm.role_title} onChange={e => setEmpForm(p => ({...p, role_title: e.target.value}))} placeholder="Foreman, PM..." /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs font-bold">Phone</Label><Input value={empForm.phone} onChange={e => setEmpForm(p => ({...p, phone: e.target.value}))} /></div>
              <div><Label className="text-xs font-bold">Email</Label><Input value={empForm.email} onChange={e => setEmpForm(p => ({...p, email: e.target.value}))} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs font-bold">Department</Label>
                <Select value={empForm.department} onValueChange={v => setEmpForm(p => ({...p, department: v}))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{DEPTS.map(d => <SelectItem key={d} value={d} className="capitalize">{d}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs font-bold">Hire Date</Label><Input type="date" value={empForm.hire_date} onChange={e => setEmpForm(p => ({...p, hire_date: e.target.value}))} /></div>
            </div>
            <div className="p-3 bg-[#f5f9fb] rounded-lg border border-[#d4e6ef]/50">
              <Label className="text-xs font-bold text-[#2a6496]">Pay Structure</Label>
              <div className="grid grid-cols-2 gap-3 mt-2">
                <div>
                  <Label className="text-[10px] font-bold">Pay Type</Label>
                  <Select value={empForm.pay_type} onValueChange={v => setEmpForm(p => ({...p, pay_type: v}))}>
                    <SelectTrigger data-testid="emp-pay-type"><SelectValue /></SelectTrigger>
                    <SelectContent>{Object.entries(PAY_TYPES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                {empForm.pay_type === 'hourly' && <div><Label className="text-[10px] font-bold">Hourly Rate ($)</Label><Input type="number" step="0.50" value={empForm.pay_rate} onChange={e => setEmpForm(p => ({...p, pay_rate: e.target.value}))} data-testid="emp-rate" /></div>}
                {empForm.pay_type === 'salary' && <div><Label className="text-[10px] font-bold">Annual Salary ($)</Label><Input type="number" value={empForm.salary_annual} onChange={e => setEmpForm(p => ({...p, salary_annual: e.target.value}))} /></div>}
                {empForm.pay_type === 'piece_rate' && <div><Label className="text-[10px] font-bold">Rate Per Piece ($)</Label><Input type="number" step="0.01" value={empForm.piece_rate_per_unit} onChange={e => setEmpForm(p => ({...p, piece_rate_per_unit: e.target.value}))} /></div>}
                {empForm.pay_type === 'commission' && <div><Label className="text-[10px] font-bold">Commission %</Label><Input type="number" step="0.5" value={empForm.commission_pct} onChange={e => setEmpForm(p => ({...p, commission_pct: e.target.value}))} /></div>}
              </div>
              <div className="grid grid-cols-2 gap-3 mt-2">
                <div><Label className="text-[10px] font-bold">Filing Status</Label>
                  <Select value={empForm.filing_status} onValueChange={v => setEmpForm(p => ({...p, filing_status: v}))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="single">Single</SelectItem><SelectItem value="married">Married</SelectItem><SelectItem value="head_of_household">Head of Household</SelectItem></SelectContent>
                  </Select>
                </div>
                <div><Label className="text-[10px] font-bold">Allowances</Label><Input type="number" min="0" value={empForm.allowances} onChange={e => setEmpForm(p => ({...p, allowances: e.target.value}))} /></div>
              </div>
            </div>
            <div><Label className="text-xs font-bold">Address</Label><Input value={empForm.address} onChange={e => setEmpForm(p => ({...p, address: e.target.value}))} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs font-bold">Emergency Contact</Label><Input value={empForm.emergency_contact} onChange={e => setEmpForm(p => ({...p, emergency_contact: e.target.value}))} /></div>
              <div><Label className="text-xs font-bold">Emergency Phone</Label><Input value={empForm.emergency_phone} onChange={e => setEmpForm(p => ({...p, emergency_phone: e.target.value}))} /></div>
            </div>
            <div><Label className="text-xs font-bold">Notes</Label><Textarea value={empForm.notes} onChange={e => setEmpForm(p => ({...p, notes: e.target.value}))} rows={2} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmpDialog(false)}>Cancel</Button>
            <Button className="bg-[#4a9ac5] hover:bg-[#3d87b3] text-white" onClick={saveEmp} disabled={!empForm.name?.trim()} data-testid="emp-save-btn">{editEmp ? 'Update' : 'Add Employee'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Time Entry Dialog */}
      <Dialog open={timeDialog} onOpenChange={setTimeDialog}>
        <DialogContent className="max-w-md" data-testid="time-dialog">
          <DialogHeader><DialogTitle>Log Time Entry</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs font-bold">Employee *</Label>
              <Select value={timeForm.employee_id} onValueChange={v => { const emp = employees.find(e => e.id === v); setTimeForm(p => ({...p, employee_id: v, employee_name: emp?.name || ''})); }}>
                <SelectTrigger data-testid="time-emp"><SelectValue placeholder="Select employee..." /></SelectTrigger>
                <SelectContent>{employees.map(e => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-bold">Project (optional)</Label>
              <Select value={timeForm.project_id || '_none'} onValueChange={v => { const p = projects.find(x => x.id === v); setTimeForm(prev => ({...prev, project_id: v === '_none' ? '' : v, project_name: p?.name || ''})); }}>
                <SelectTrigger><SelectValue placeholder="Select project..." /></SelectTrigger>
                <SelectContent><SelectItem value="_none">No Project</SelectItem>{projects.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs font-bold">Date</Label><Input type="date" value={timeForm.date} onChange={e => setTimeForm(p => ({...p, date: e.target.value}))} data-testid="time-date" /></div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label className="text-xs font-bold">Reg Hours</Label><Input type="number" step="0.5" value={timeForm.hours} onChange={e => setTimeForm(p => ({...p, hours: e.target.value}))} data-testid="time-hours" /></div>
              <div><Label className="text-xs font-bold">OT Hours</Label><Input type="number" step="0.5" value={timeForm.overtime_hours} onChange={e => setTimeForm(p => ({...p, overtime_hours: e.target.value}))} /></div>
              <div><Label className="text-xs font-bold">Pieces</Label><Input type="number" value={timeForm.pieces} onChange={e => setTimeForm(p => ({...p, pieces: e.target.value}))} /></div>
            </div>
            <div><Label className="text-xs font-bold">Description</Label><Input value={timeForm.description} onChange={e => setTimeForm(p => ({...p, description: e.target.value}))} placeholder="What was done..." /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTimeDialog(false)}>Cancel</Button>
            <Button className="bg-[#4a9ac5] hover:bg-[#3d87b3] text-white" onClick={saveTime} disabled={!timeForm.employee_id || !timeForm.date} data-testid="time-save-btn">Log Entry</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* History Detail Dialog */}
      <Dialog open={!!historyDetail} onOpenChange={() => setHistoryDetail(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="history-detail">
          <DialogHeader><DialogTitle>Payroll: {historyDetail?.period_start} — {historyDetail?.period_end}</DialogTitle></DialogHeader>
          {historyDetail && (
            <Table>
              <TableHeader><TableRow className="bg-[#f5f9fb]">
                <TableHead className="font-bold text-xs">Employee</TableHead><TableHead className="font-bold text-xs text-center">Hours</TableHead>
                <TableHead className="font-bold text-xs text-right">Gross</TableHead><TableHead className="font-bold text-xs text-right">Deductions</TableHead>
                <TableHead className="font-bold text-xs text-right">Net</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {(historyDetail.employees || []).map(emp => (
                  <TableRow key={emp.employee_id}>
                    <TableCell className="text-xs font-bold">{emp.employee_name}</TableCell>
                    <TableCell className="text-center text-xs font-mono">{emp.total_hours}</TableCell>
                    <TableCell className="text-right text-xs font-mono">${emp.gross_pay.toLocaleString()}</TableCell>
                    <TableCell className="text-right text-xs font-mono text-red-500">-${emp.deductions?.total_deductions?.toLocaleString()}</TableCell>
                    <TableCell className="text-right text-xs font-mono font-bold text-emerald-700">${emp.net_pay.toLocaleString()}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
